import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { API_URL_All } from "../api";
import axiosInstance from "../axiosInstance";

import "../css/AdminHeader.css";
import "@fortawesome/fontawesome-free/css/all.min.css";

import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import { logoutUser } from "../redux/userSlice";
import { FiLogOut, FiMessageSquare, FiSettings, FiSliders, FiUser } from "react-icons/fi";
import {
  resetFilter,
  resetFilterFile,
  setIsFilterAppliedFile,
  setIsFilterAppliedOrder,
} from "../redux/filterReducer";
import { resetUserFilters, setIsFilterApplied } from "../redux/userFilterSlice";
import * as general from "../libs/general";
import { PersonIcon, WarningIcon, BellIcon } from "../components/tickets/Tickets";
import { buildAdminChatWebSocketUrl } from "../chatAdminApi";
import {
  ORDER_DIVISION_MENU_VIEW_FEATURES,
  NOTIFY_NCC_RATING_REMINDER_FEATURE,
  NOTIFY_TRANSFER_REQUEST_INBOX_FEATURE,
  CUSTOMERS_DIVISION_FEATURE,
  CUSTOMER_FILTER_DIVISION_FEATURES,
  CSKH_DIVISION_FEATURES,
  SYSTEM_SETTINGS_DIVISION_FEATURE,
} from "../libs/customerCreatePermission";

const sortChatConversations = (items = []) =>
  [...items].sort((left, right) => {
    const leftTime = left?.lastMessageAt ? new Date(left.lastMessageAt).getTime() : 0;
    const rightTime = right?.lastMessageAt ? new Date(right.lastMessageAt).getTime() : 0;
    return rightTime - leftTime || (right.id || 0) - (left.id || 0);
  });

const sortRequestersNewestFirst = (items = []) =>
  [...items].sort((left, right) => {
    const leftTime = left?.createdAt ? new Date(left.createdAt).getTime() : 0;
    const rightTime = right?.createdAt ? new Date(right.createdAt).getTime() : 0;
    return rightTime - leftTime || (right.id || 0) - (left.id || 0);
  });

const resolveUserAvatarSrc = (userPath) => {
  if (!userPath) return "";
  if (userPath.startsWith("http://") || userPath.startsWith("https://")) return userPath;
  const base = API_URL_All.replace(/\/$/, "");
  const path = userPath.startsWith("/") ? userPath : `/${userPath}`;
  return `${base}${path}`;
};

const normalizeFeatureName = (value) =>
  String(value || "")
    .normalize("NFC")
    .replace(/\s+/g, " ")
    .replace(/\s*\/\s*/g, " / ")
    .trim()
    .toLowerCase();

const BackendHeader = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const currentUser = useSelector((state) => state.user.user) || {};
  const [effectiveUserFlags, setEffectiveUserFlags] = useState({ isAdmin: false, isMember: false });
  const config = general.getLocalStorage("user_config");
  const [divisionPermissions, setDivisionPermissions] = useState([]);
  const [divisionPermissionsLoaded, setDivisionPermissionsLoaded] = useState(false);
  const [operationObj, setOperationObj] = useState({});
  const [pageData, setPageData] = useState({ content: [] });
  const [pageRequesterData, setPageRequesterData] = useState({ content: [] });
  const [requesterUnreadOnly, setRequesterUnreadOnly] = useState(false);
  const [requesterInboxExpanded, setRequesterInboxExpanded] = useState(false);
  const [requesterMenuOpen, setRequesterMenuOpen] = useState(false);
  const [chatConversations, setChatConversations] = useState([]);
  const [chatConnectionStatus, setChatConnectionStatus] = useState("dang-ket-noi");
  const [pendingContactExplorationCount, setPendingContactExplorationCount] = useState(0);

  const requesterLoadSeqRef = useRef(0);
  const requesterContentRef = useRef([]);
  const requesterReplaceInFlightRef = useRef(false);
  const headerChatSocketRef = useRef(null);
  const headerChatReconnectRef = useRef(null);
  const headerChatPollRef = useRef(null);
  const headerChatRefreshTimerRef = useRef(null);

  const unreadChatCount = chatConversations.reduce(
    (total, conversation) => total + (conversation.unreadAdminCount || 0),
    0
  );

  const headerGuestSignalTotal = unreadChatCount + pendingContactExplorationCount;

  const recentUnreadChats = chatConversations
    .filter((conversation) => (conversation.unreadAdminCount || 0) > 0)
    .sort((left, right) => {
      const leftTime = left?.lastMessageAt ? new Date(left.lastMessageAt).getTime() : 0;
      const rightTime = right?.lastMessageAt ? new Date(right.lastMessageAt).getTime() : 0;
      return rightTime - leftTime;
    })
    .slice(0, 5);

  const handleLogout = () => {
    dispatch(logoutUser());
    dispatch(resetFilter());
    dispatch(resetUserFilters());
    dispatch(setIsFilterApplied(false));
    dispatch(setIsFilterAppliedOrder(false));
    sessionStorage.removeItem("currentPageUser");
    dispatch(resetFilterFile());
    sessionStorage.removeItem("itemsPerPageUser");
    dispatch(setIsFilterAppliedFile(false));
    sessionStorage.removeItem("filtersUser");
    sessionStorage.removeItem("currentPage");
    sessionStorage.removeItem("itemsPerPage");
    sessionStorage.removeItem("filterData");
    sessionStorage.removeItem("isFilterApplied");
    sessionStorage.removeItem("filters");
    navigate("/dang-nhap");
  };

  useEffect(() => {
    refreshOperationInfo();

    const intervalId = setInterval(refreshOperationInfo, 30000);
    return () => clearInterval(intervalId);
  }, []);

  useEffect(() => {
    const handleChatSync = (event) => {
      const nextConversations = Array.isArray(event.detail?.conversations)
        ? sortChatConversations(event.detail.conversations)
        : [];
      setChatConversations(nextConversations);
      setChatConnectionStatus(event.detail?.connectionStatus || "truc-tuyen");
    };

    window.addEventListener("admin-chat-conversations-sync", handleChatSync);
    return () => {
      window.removeEventListener("admin-chat-conversations-sync", handleChatSync);
    };
  }, []);

  const loadPendingContactExplorationCount = async () => {
    try {
      const response = await axiosInstance.get("/api/contact-exploration/pending-count");
      if (response?.data?.success !== true || typeof response.data.count !== "number") {
        return;
      }
      setPendingContactExplorationCount(response.data.count);
    } catch (error) {
      console.error(error);
    }
  };

  const scheduleHeaderGuestSignalsRefresh = () => {
    if (headerChatRefreshTimerRef.current) {
      window.clearTimeout(headerChatRefreshTimerRef.current);
    }
    headerChatRefreshTimerRef.current = window.setTimeout(() => {
      headerChatRefreshTimerRef.current = null;
      loadPendingContactExplorationCount();
    }, 120);
  };

  const startHeaderChatPolling = (shouldStart) => {
    window.clearInterval(headerChatPollRef.current);
    if (!shouldStart) return;
    headerChatPollRef.current = window.setInterval(() => {
      loadPendingContactExplorationCount();
    }, 5000);
  };

  const connectHeaderChatSocket = () => {
    window.clearTimeout(headerChatReconnectRef.current);
    headerChatSocketRef.current?.close();

    try {
      const socket = new WebSocket(buildAdminChatWebSocketUrl());
      headerChatSocketRef.current = socket;
      setChatConnectionStatus("dang-ket-noi");

      socket.onopen = () => {
        setChatConnectionStatus("truc-tuyen");
        startHeaderChatPolling(false);
        socket.send(JSON.stringify({
          type: "subscribe_admin",
          accessToken: localStorage.getItem("token") || "",
        }));
        loadPendingContactExplorationCount();
      };

      socket.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (!data?.type || data.type === "pong" || data.type === "subscribed") {
            return;
          }
          if (data.type === "error") {
            return;
          }
          if (data.type === "contact.exploration.created") {
            scheduleHeaderGuestSignalsRefresh();
          }
        } catch (err) {
          console.error("Admin header chat socket message", err);
        }
      };

      socket.onerror = () => {
        setChatConnectionStatus("mat-ket-noi");
        startHeaderChatPolling(true);
      };

      socket.onclose = () => {
        if (headerChatSocketRef.current === socket) {
          headerChatSocketRef.current = null;
        }
        setChatConnectionStatus("mat-ket-noi");
        startHeaderChatPolling(true);
        headerChatReconnectRef.current = window.setTimeout(connectHeaderChatSocket, 4000);
      };
    } catch (e) {
      setChatConnectionStatus("mat-ket-noi");
      startHeaderChatPolling(true);
    }
  };

  useEffect(() => {
    loadPendingContactExplorationCount();
    connectHeaderChatSocket();

    return () => {
      window.clearTimeout(headerChatReconnectRef.current);
      window.clearTimeout(headerChatRefreshTimerRef.current);
      window.clearInterval(headerChatPollRef.current);
      headerChatSocketRef.current?.close();
      headerChatSocketRef.current = null;
    };
  }, []);

  const refreshOperationInfo = async () => {
    var requestApi = `/api/users/getOperationInfo`;
    try {
      if (window.callingApi9) return;
      window.callingApi9 = true;
      var response = await axiosInstance.get(requestApi);

      var obj = response.data.data;
      general.setLocalStorage("operation_info", obj);
      setOperationObj(obj);
    } catch (error) {
      console.error(error);
    } finally {
      window.callingApi9 = false;
    }
  };

  const loadNotifies = async () => {
    var requestApi = `${API_URL_All}/api/providers/searchFeedbackOrders`;
    try {
      if (window.callingApi) return;
      window.callingApi = true;
      var response = await axiosInstance.get(requestApi);
      setPageData(response.data);
    } catch (error) {
      console.error(error);
    } finally {
      window.callingApi = false;
    }
  };

  const navigateToRequesters = (url) => {
    const dropdownMenu = document.querySelector("#userDropdown_003 + .dropdown-menu");
    const toggle = document.getElementById("userDropdown_003");

    setRequesterMenuOpen(false);
    dropdownMenu?.classList.remove("show");
    toggle?.setAttribute("aria-expanded", "false");
    navigate(url);
  };

  const navigateToChatInbox = () => {
    navigate("/admin/chat-inbox");
  };

  const navigateToContactExploration = () => {
    navigate("/contact-exploration");
  };

  const viewMoreRequesters = () => {
    setRequesterInboxExpanded(true);
    const page = pageRequesterData.page;
    const currentPage = page?.pageable?.pageNumber ?? page?.number;
    const totalPages = page?.totalPages;
    if (page && Number.isInteger(currentPage) && Number.isInteger(totalPages) && currentPage < totalPages - 1) {
      loadUserRequesters(currentPage + 1, { append: true, unreadOnly: requesterUnreadOnly });
    }
  };

  const applyRequesterFilter = (unreadOnly) => {
    setRequesterUnreadOnly(unreadOnly);
    loadUserRequesters(0, { append: false, unreadOnly });
  };

  const loadUserRequesters = async (pageNumber, options = {}) => {
    const append = options.append === true;
    const unreadOnly = options.unreadOnly ?? requesterUnreadOnly;
    const params = new URLSearchParams();
    if (append && general.isNumber(pageNumber)) {
      params.set("page", String(pageNumber));
    }
    if (unreadOnly) {
      params.set("hasRead", "false");
    }
    const query = params.toString();
    var requestApi = `${API_URL_All}/api/import-orders/searchRequesters${query ? `?${query}` : ""}`;
    if (append && requesterReplaceInFlightRef.current) return;
    if (!append) {
      requesterReplaceInFlightRef.current = true;
      setRequesterInboxExpanded(false);
    }
    const seq = ++requesterLoadSeqRef.current;
    try {
      var response = await axiosInstance.get(requestApi);
      if (seq !== requesterLoadSeqRef.current) return;

      var pageModel = response.data;
      const incoming = pageModel.page?.content || [];
      const combined = append ? [...requesterContentRef.current, ...incoming] : incoming;
      const sorted = sortRequestersNewestFirst(combined);
      const groups = sorted.filter((item) => item.grouped);
      const history = sorted.filter((item) => !item.grouped);
      const pinned = [...groups, ...history];
      requesterContentRef.current = pinned;
      if (pageModel.page) {
        pageModel.page.content = pinned;
      }
      setPageRequesterData(pageModel);
    } catch (error) {
      if (seq === requesterLoadSeqRef.current) {
        console.error(error);
        if (!append) {
          requesterContentRef.current = [];
          setPageRequesterData({ page: { content: [], totalPages: 0 } });
        }
      }
    } finally {
      if (seq === requesterLoadSeqRef.current) {
        requesterReplaceInFlightRef.current = false;
      }
    }
  };

  useEffect(() => {
    const handleEvent = () => {
      refreshOperationInfo();
      if (location.pathname === "/contact-exploration") {
        loadPendingContactExplorationCount();
      }
    };
    window.addEventListener("refresh-operation-info", handleEvent);
    return () => {
      window.removeEventListener("refresh-operation-info", handleEvent);
    };
  }, [location.pathname]);

  const chatStatusLabel =
    chatConnectionStatus === "truc-tuyen"
      ? "Realtime"
      : chatConnectionStatus === "mat-ket-noi"
      ? "Fallback"
      : chatConnectionStatus === "dang-ket-noi"
      ? "Dang ket noi"
      : "Theo yeu cau";

  const isMemberUser = effectiveUserFlags.isMember !== false;
  const isAdminUser = effectiveUserFlags.isAdmin === true;
  const allowedFeatureSet = useMemo(
    () =>
      new Set(
        (divisionPermissions || [])
          .filter((p) => p?.view)
          .map((p) => normalizeFeatureName(p?.feature))
          .filter(Boolean)
      ),
    [divisionPermissions]
  );
  const shouldApplyPermissionMenu = !isAdminUser && isMemberUser && divisionPermissionsLoaded;
  const canViewFeature = (featureName) => {
    if (isAdminUser) return true;
    if (!isMemberUser) return !shouldApplyPermissionMenu;
    if (!divisionPermissionsLoaded) return false;
    return allowedFeatureSet.has(normalizeFeatureName(featureName));
  };

  const canViewWebsiteContact = canViewFeature("WEBSITE / Liên hệ khách hàng");
  const canViewWebsiteNews = canViewFeature("WEBSITE / Quản lý bài viết");
  const canViewUserCustomer =
    canViewFeature(CUSTOMERS_DIVISION_FEATURE) ||
    CUSTOMER_FILTER_DIVISION_FEATURES.some((f) => canViewFeature(f));
  const canViewUserPartner = canViewFeature("NGƯỜI DÙNG / Đối tác");
  const canViewUserDepartments =
    canViewFeature("NGƯỜI DÙNG / Phân quyền") || canViewFeature("NGƯỜI DÙNG / Phòng ban");
  /** Menu Danh sách users — chỉ khi bộ phận có quyền xem chức năng này (không gộp với Phân quyền). */
  const canViewUserAdminList = canViewFeature("NGƯỜI DÙNG / Danh sách users");
  const canViewProduct =
    canViewFeature("SẢN PHẨM / Sản phẩm cá nhân") ||
    canViewFeature("SẢN PHẨM / Sản phẩm dùng chung") ||
    canViewFeature("SẢN PHẨM / Sản phẩm");
  const canViewCategory = canViewFeature("SẢN PHẨM / Danh mục");
  const canViewProvider = canViewFeature("SẢN PHẨM / Nhà cung cấp");
  const canViewOrder = ORDER_DIVISION_MENU_VIEW_FEATURES.some((f) => canViewFeature(f));
  const canViewReconcileNormal = canViewFeature("ĐỐI SOÁT / Khách hàng thường");
  const canViewReconcileVat = canViewFeature("ĐỐI SOÁT / Khách hàng VAT");
  const canViewReconcilePartnerNormal = canViewFeature("ĐỐI SOÁT / Đối tác thường");
  const canViewReconcilePartnerVat = canViewFeature("ĐỐI SOÁT / Đối tác VAT");
  const canViewChat =
    CSKH_DIVISION_FEATURES.some((f) => canViewFeature(f)) || canViewFeature("CHAT");
  /** Tạm ẩn CHAT trên menu top — portal nhúng tại /admin/omnichannel. */
  const showTopMenuChat = true;
  const canViewSettings = canViewFeature("MỞ RỘNG / Cài đặt");
  const canViewSystemSettings = canViewFeature(SYSTEM_SETTINGS_DIVISION_FEATURE);
  const canViewAccountingByPrefix = (prefix) => {
    if (canViewFeature("KẾ TOÁN")) return true;
    if (canViewFeature(prefix)) return true;
    const n = normalizeFeatureName(prefix);
    for (const f of allowedFeatureSet) {
      if (f === n || f.startsWith(`${n} / `)) return true;
    }
    return false;
  };
  const canViewAccountingReport =
    canViewAccountingByPrefix("KẾ TOÁN / Báo cáo")
    || canViewFeature("KẾ TOÁN / Báo cáo / Doanh thu")
    || canViewFeature("KẾ TOÁN / Báo cáo / Chi phí")
    || canViewFeature("KẾ TOÁN / Báo cáo / Lợi nhuận")
    || canViewFeature("KẾ TOÁN / Báo cáo / Tài sản");
  const canViewAccountingCashFlow =
    canViewFeature("KẾ TOÁN / Dòng tiền")
    || canViewFeature("KẾ TOÁN / Báo cáo / Dòng tiền")
    || canViewFeature("KẾ TOÁN");
  const canViewAccountingDebt = canViewAccountingByPrefix("KẾ TOÁN / Công nợ");
  const canViewAccountingVoucher = canViewAccountingByPrefix("KẾ TOÁN / Phiếu thu/chi");
  const canViewAccountingConfig = canViewAccountingByPrefix("KẾ TOÁN / Cấu hình");
  const canViewAccountingHr =
    canViewAccountingByPrefix("KẾ TOÁN / Sổ chi")
    || canViewFeature("KẾ TOÁN / Lương & Tạm ứng")
    || canViewFeature("KẾ TOÁN");
  const canViewAccounting =
    canViewAccountingReport || canViewAccountingCashFlow || canViewAccountingDebt || canViewAccountingVoucher
    || canViewAccountingConfig || canViewAccountingHr;
  const canViewWarehouseGroup = canViewFeature("KHO VẬN");
  const canViewWarehouseScanIn = canViewWarehouseGroup || canViewFeature("KHO VẬN / Quét nhập kho VN");
  const canViewWarehouseScanOut = canViewWarehouseGroup || canViewFeature("KHO VẬN / Quét xuất kho VN");
  const canViewWarehouseUnclaimed = canViewWarehouseGroup || canViewFeature("KHO VẬN / Đơn vô chủ");
  const canViewWarehouse = canViewWarehouseScanIn || canViewWarehouseScanOut || canViewWarehouseUnclaimed;
  const canNotifyNccRatingReminder = canViewFeature(NOTIFY_NCC_RATING_REMINDER_FEATURE);
  const canReceiveTransferRequests = canViewFeature(NOTIFY_TRANSFER_REQUEST_INBOX_FEATURE);

  useEffect(() => {
    const toggle = document.getElementById("userDropdown_003");
    if (!toggle) return undefined;
    const onShow = () => setRequesterMenuOpen(true);
    const onHide = () => setRequesterMenuOpen(false);
    toggle.addEventListener("show.bs.dropdown", onShow);
    toggle.addEventListener("hide.bs.dropdown", onHide);
    return () => {
      toggle.removeEventListener("show.bs.dropdown", onShow);
      toggle.removeEventListener("hide.bs.dropdown", onHide);
    };
  }, [canReceiveTransferRequests]);

  useEffect(() => {
    const loadDivisionPermissions = async () => {
      try {
        const response = await axiosInstance.get("/api/users/myDivisionPermissions");
        setDivisionPermissions(response?.data?.data || []);
      } catch (error) {
        setDivisionPermissions([]);
      } finally {
        setDivisionPermissionsLoaded(true);
      }
    };
    loadDivisionPermissions();
  }, []);

  useEffect(() => {
    const loadUserFlags = async () => {
      try {
        const response = await axiosInstance.get("/api/users/myFlags");
        const flags = response?.data?.data || {};
        setEffectiveUserFlags({
          isAdmin: flags?.isAdmin === true,
          isMember: flags?.isMember === true,
        });
      } catch (error) {
        setEffectiveUserFlags({
          isAdmin: currentUser?.isAdmin === true,
          isMember: currentUser?.isMember !== false,
        });
      }
    };
    loadUserFlags();
  }, [currentUser?.isAdmin, currentUser?.isMember]);

  const requesterPage = pageRequesterData.page;
  const requesterCurrentPage = requesterPage?.pageable?.pageNumber ?? requesterPage?.number;
  const requesterHasMore = Number.isInteger(requesterCurrentPage) && requesterCurrentPage < (requesterPage?.totalPages ?? 0) - 1;

  return (
    <nav className="fixed-top navbar navbar-expand-lg navbar-dark bg-dark menu_top">
      <div className="container-fluid">
        <Link className="navbar-brand fw-bold" to="/admin-welcome">Home</Link>
        <button
          className="navbar-toggler"
          type="button"
          data-bs-toggle="collapse"
          data-bs-target="#navbarNavDropdown"
          aria-controls="navbarNavDropdown"
          aria-expanded="false"
          aria-label="Toggle navigation"
        >
          <span className="navbar-toggler-icon"></span>
        </button>

        <div className="collapse navbar-collapse" id="navbarNavDropdown">
          <ul className="navbar-nav mx-auto nav-center">
            {(canViewWebsiteContact || canViewWebsiteNews) && (
            <li className="nav-item dropdown">
              <a
                className="nav-link dropdown-toggle"
                href="#"
                id="navbarDropdownMenuLink"
                role="button"
                data-bs-toggle="dropdown"
                aria-expanded="false"
              >
                WEBSITE
              </a>
              <ul className="dropdown-menu" aria-labelledby="navbarDropdownMenuLink">
                {canViewWebsiteContact && <li><Link className="dropdown-item" to="/contact-exploration">Liên hệ khách hàng</Link></li>}
                {canViewWebsiteNews && <li><Link className="dropdown-item" to="/admin/news-list">Quản lý bài viết</Link></li>}
              </ul>
            </li>
            )}
            {(canViewUserCustomer || canViewUserPartner || canViewUserDepartments || canViewUserAdminList) && (
            <li className="nav-item dropdown">
              <a className="nav-link dropdown-toggle" href="#" id="nav4" role="button" data-bs-toggle="dropdown" aria-expanded="false">
                HỆ THỐNG
              </a>
              <ul className="dropdown-menu" aria-labelledby="nav4">
                {canViewUserCustomer && <li><Link className="dropdown-item" to="/quan-li-khach-hang">Khách hàng</Link></li>}
                {canViewUserPartner && <li><Link className="dropdown-item" to="/contacts">Đối tác</Link></li>}
                {canViewUserAdminList && <li><Link className="dropdown-item" to="/admin-users">Thành viên</Link></li>}
                {canViewUserDepartments && <li><Link className="dropdown-item" to="/permission">Phân quyền</Link></li>}
              </ul>
            </li>
            )}
            {(canViewProduct || canViewCategory || canViewProvider) && (
            <li className="nav-item dropdown">
              <a
                className="nav-link dropdown-toggle"
                href="#"
                id="navbarDropdownMenuLink3"
                role="button"
                data-bs-toggle="dropdown"
                aria-expanded="false"
              >
                SẢN PHẨM
              </a>
              <ul className="dropdown-menu" aria-labelledby="navbarDropdownMenuLink3">
                {canViewProduct && <li><Link className="dropdown-item" to="/products">Sản phẩm</Link></li>}
                {canViewCategory && <li><Link className="dropdown-item" to="/categories">Danh mục</Link></li>}
                {canViewProvider && <li><Link className="dropdown-item" to="/providers">Nhà cung cấp</Link></li>}
              </ul>
            </li>
            )}
            <li className="nav-item d-none">
              <Link className="nav-link" to="/products">SẢN PHẨM 2</Link>
            </li>
            {canViewOrder && (
            <li className="nav-item dropdown">
              <a
                className="nav-link dropdown-toggle"
                href="#"
                id="navbarDropdownOrders"
                role="button"
                data-bs-toggle="dropdown"
                aria-expanded="false"
              >
                ĐƠN HÀNG
              </a>
              <ul className="dropdown-menu" aria-labelledby="navbarDropdownOrders">
                <li><Link className="dropdown-item" to={`/quan-li-don-hang?lines=${encodeURIComponent('Line Thường;TMĐT;Line XT')}`}>Đơn Thường</Link></li>
                <li><Link className="dropdown-item" to={`/quan-li-don-hang?lines=${encodeURIComponent('Line VAT')}`}>Đơn VAT</Link></li>
                <li><Link className="dropdown-item" to="/quan-li-don-tth">Đơn TTH</Link></li>
              </ul>
            </li>
            )}
            {canViewWarehouse && (
            <li className="nav-item dropdown">
              <a
                className="nav-link dropdown-toggle"
                href="#"
                id="navbarDropdownWarehouse"
                role="button"
                data-bs-toggle="dropdown"
                aria-expanded="false"
              >
                KHO VẬN
              </a>
              <ul className="dropdown-menu" aria-labelledby="navbarDropdownWarehouse">
                {canViewWarehouseScanIn && <li><Link className="dropdown-item" to="/kho-van/quet-nhap">Quét nhập kho VN</Link></li>}
                {canViewWarehouseScanOut && <li><Link className="dropdown-item" to="/kho-van/quet-xuat">Quét xuất kho VN</Link></li>}
                {canViewWarehouseUnclaimed && <li><Link className="dropdown-item" to="/kho-van/don-vo-chu">Đơn vô chủ</Link></li>}
              </ul>
            </li>
            )}            
            {(canViewReconcileNormal ||
              canViewReconcileVat ||
              canViewReconcilePartnerNormal ||
              canViewReconcilePartnerVat ||
              canViewOrder) && (
            <li className="nav-item dropdown">
              <a
                className="nav-link dropdown-toggle"
                href="#"
                id="navbarDropdownMenuLink2"
                role="button"
                data-bs-toggle="dropdown"
                aria-expanded="false"
              >
                ĐỐI SOÁT
              </a>
              <ul className="dropdown-menu" aria-labelledby="navbarDropdownMenuLink2">
                {canViewReconcileNormal && <li><Link className="dropdown-item" to="/quan-li-doi-soat">Khách hàng thường</Link></li>}
                {canViewReconcileVat && <li><Link className="dropdown-item" to="/doi-soat-don-hang">Khách hàng VAT</Link></li>}
                {canViewReconcilePartnerNormal && (
                  <li><Link className="dropdown-item" to="/don-hang-doi-tac">Đối tác thường</Link></li>
                )}
                {canViewReconcilePartnerVat && (
                  <li><Link className="dropdown-item" to="/don-hang-vat-doi-tac">Đối tác VAT</Link></li>
                )}
                {canViewOrder && <li><Link className="dropdown-item" to="/quan-li-doi-soat-tth">Đối tác TTH</Link></li>}
              </ul>
            </li>
            )}
            {canViewAccounting && (
            <li className="nav-item dropdown">
              <a
                className="nav-link dropdown-toggle"
                href="#"
                id="navbarDropdownAccounting"
                role="button"
                data-bs-toggle="dropdown"
                aria-expanded="false"
              >
                KẾ TOÁN
              </a>
              <ul className="dropdown-menu" aria-labelledby="navbarDropdownAccounting">
                {(canViewAccountingReport || canViewAccountingCashFlow) && <li><Link className="dropdown-item" to="/ke-toan/bao-cao">Báo cáo</Link></li>}
                {canViewAccountingVoucher && <li><Link className="dropdown-item" to="/ke-toan/phieu-thu-chi">Phiếu thu/chi</Link></li>}
                {canViewAccountingDebt && <li><Link className="dropdown-item" to="/ke-toan/cong-no">Công nợ</Link></li>}
                {canViewAccountingHr && <li><Link className="dropdown-item" to="/ke-toan/nhan-su">Sổ chi</Link></li>}
                {canViewAccountingConfig && <li><Link className="dropdown-item" to="/ke-toan/cau-hinh">Cấu hình</Link></li>}
              </ul>
            </li>
            )}
            {canViewChat && showTopMenuChat && (
            <li className="nav-item">
                <a
                  className="nav-link"
                  href="#/admin/omnichannel"
                  onClick={(event) => {
                    event.preventDefault();
                    const hash = "#/admin/omnichannel";
                    const nextUrl = `${window.location.pathname}${window.location.search}${hash}`;
                    if (window.location.hash === hash) {
                      window.location.reload();
                      return;
                    }
                    window.location.assign(nextUrl);
                    window.location.reload();
                  }}
                >
                  <span>CSKH</span>
                </a>
            </li>
            )}
          </ul>

          <ul className="navbar-nav mw-68 flex-row justify-content-end align-items-center">
            <li className="nav-item position-relative d-none">
              <a id="userDropdown_004" className="nav-link" data-bs-toggle="dropdown">
                <span data-bell={operationObj.overdueCount}>
                  <WarningIcon />
                </span>
              </a>
              <ul className="bell-area dropdown-menu dropdown-menu-end" aria-labelledby="userDropdown_004">
                {operationObj.listProgress && operationObj.listProgress.map((item, index) => (
                  <li onClick={() => navigateToRequesters(`/quan-li-don-hang?overdueId=${item.statusId}`)} key={index}>
                    <span className="bell-feeback">{item.name} - {item.count} đơn</span>
                  </li>
                ))}
              </ul>
            </li>

            {canViewChat && showTopMenuChat && (
            <li className="nav-item position-relative admin-chat-shortcut dropdown">
              <a
                id="userDropdown_chat"
                className="nav-link admin-chat-shortcut__link"
                data-bs-toggle="dropdown"
                data-bs-auto-close="outside"
                onClick={() => {
                  loadPendingContactExplorationCount();
                }}
                aria-label="Thong bao chat va lien he khach"
                title={chatStatusLabel}
              >
                <span
                  data-bell={headerGuestSignalTotal > 99 ? "99+" : headerGuestSignalTotal || undefined}
                  className={`admin-chat-shortcut__icon ${headerGuestSignalTotal > 0 ? "has-unread" : ""}`}
                >
                  <FiMessageSquare size={18} />
                </span>
              </a>
              <ul className="bell-area dropdown-menu dropdown-menu-end admin-chat-shortcut__menu" aria-labelledby="userDropdown_chat">
                <li className="admin-chat-shortcut__menu-header">
                  <span className="admin-chat-shortcut__menu-title">{"Liên hệ"}</span>
                  <span className="admin-chat-shortcut__menu-meta">{chatStatusLabel}</span>
                </li>
                {pendingContactExplorationCount > 0 ? (
                  <li onClick={navigateToContactExploration}>
                    <span className="bell-feeback">
                      {pendingContactExplorationCount > 1
                        ? `${pendingContactExplorationCount} yêu cầu liên hệ chưa xác nhận`
                        : "1 yêu cầu liên hệ chưa xác nhận"}
                    </span>
                    <span className="bell-des">Form liên hệ trên website</span>
                  </li>
                ) : null}
                <li
                  className={`admin-chat-shortcut__subheader${
                    pendingContactExplorationCount > 0 ? " admin-chat-shortcut__subheader--divider" : ""
                  }`}
                >
                  <span className="admin-chat-shortcut__subheader-text">Tin nhắn chat</span>
                </li>
                {recentUnreadChats.length > 0 ? (
                  recentUnreadChats.map((item) => (
                    <li onClick={navigateToChatInbox} key={item.publicToken}>
                      <span className="bell-feeback">
                        {item.visitorName || "Khách vãng lai"}
                        {item.unreadAdminCount > 1 ? ` - ${item.unreadAdminCount} tin mới` : " - tin nhắn mới"}
                      </span>
                      <span className="bell-des">{item.lastMessagePreview || "Khách vừa gửi tin nhắn"}</span>
                    </li>
                  ))
                ) : (
                  // <li className="admin-chat-shortcut__empty">Chưa có tin nhắn chưa đọc</li>
                  <>  </>
                )}
                <li className="text-center admin-chat-shortcut__footer">
                </li>
              </ul>
            </li>
            )}

            {canReceiveTransferRequests && (
            <li className="nav-item position-relative">
              <a id="userDropdown_003" className="nav-link" data-bs-toggle="dropdown" onClick={loadUserRequesters} data-bs-auto-close="outside">
                <span data-bell={operationObj.requestedCount}>
                  <PersonIcon />
                </span>
              </a>
              <ul
                className={`bell-area dropdown-menu dropdown-menu-end requester-inbox${requesterMenuOpen ? " show" : ""}${requesterInboxExpanded ? " requester-inbox--expanded" : ""}`}
                aria-labelledby="userDropdown_003"
                onMouseDown={(event) => event.stopPropagation()}
                onClick={(event) => event.stopPropagation()}
              >
                <li className="requester-inbox__toolbar">
                  <button
                    type="button"
                    className={`requester-inbox__filter${requesterUnreadOnly ? "" : " is-active"}`}
                    onClick={() => applyRequesterFilter(false)}
                  >
                    Tất cả
                  </button>
                  <button
                    type="button"
                    className={`requester-inbox__filter${requesterUnreadOnly ? " is-active" : ""}`}
                    onClick={() => applyRequesterFilter(true)}
                  >
                    Chưa đọc
                  </button>
                </li>
                {pageRequesterData.page && pageRequesterData.page.content && pageRequesterData.page.content.length === 0 && (
                  <li className="requester-inbox__empty">
                    {requesterUnreadOnly ? "Không có yêu cầu chưa đọc" : "Chưa có yêu cầu"}
                  </li>
                )}
                {pageRequesterData.page && pageRequesterData.page.content && pageRequesterData.page.content.map((item) => (
                  <li
                    onClick={() => navigateToRequesters(item.grouped
                      ? (item.readBatchId
                        ? `/quan-li-don-hang?readBatchId=${item.readBatchId}`
                        : `/quan-li-don-hang?pendingUserId=${item.userId}`)
                      : `/quan-li-don-hang?requesterId=${item.id}`)}
                    key={item.grouped ? (item.readBatchId ? `batch-${item.readBatchId}` : `group-${item.userId}`) : item.id}
                    className={`read_${item.hasRead}`}
                  >
                    <span className="bell-feeback">Yêu cầu từ: {item.customerCode || item.name}</span>
                    <span className="bell-des" title={item.grouped ? (item.readBatchId ? `readBatchId: ${item.readBatchId}` : `pendingUserId: ${item.userId}`) : `requesterId: ${item.id}`}>
                      {general.formatDateTime2(item.createdAt)}: Số đơn: {item.orderCount}
                      {item.grouped ? ` (${item.requestCount} yêu cầu)` : ""}
                    </span>
                  </li>
                ))}
                {requesterHasMore && (
                  <li className="requester-inbox__more text-center">
                    <button type="button" className="requester-inbox__more-btn" onMouseDown={(event) => event.stopPropagation()} onClick={(event) => { event.preventDefault(); event.stopPropagation(); viewMoreRequesters(); }}>
                      Xem thêm
                    </button>
                  </li>
                )}
              </ul>
            </li>
            )}
            {canNotifyNccRatingReminder && (
            <li className="nav-item position-relative">
              <a id="userDropdown_002" className="nav-link" data-bs-toggle="dropdown" onClick={() => loadNotifies()}>
                <span data-bell={operationObj.notifyCount}>
                  <BellIcon />
                </span>
              </a>
              <ul className="bell-area dropdown-menu dropdown-menu-end" aria-labelledby="userDropdown_002">
                {pageData.page && pageData.page.content && pageData.page.content.map((item, index) => (
                  <li onClick={() => navigate(`/provider/${item.providerId}?orderId=${item.orderId}`)} key={index}>
                    <span className="bell-feeback">Đã giao: {item.productName}</span>
                    <span className="bell-des" title={`orderId: ${item.orderId}`}>Đánh giá NCC ngay</span>
                  </li>
                ))}
              </ul>
            </li>
            )}
            <li className="nav-item dropdown d-flex align-items-center">
              <a className="nav-link dropdown-toggle d-inline-flex align-items-center" href="#" id="userDropdown" role="button" data-bs-toggle="dropdown" aria-expanded="false">
                <span className="avatar-frame">
                  <img src={resolveUserAvatarSrc(config.userPath)} alt="User Avatar" className="avatar" data-user-fullname={config.name} />
                </span>
              </a>
              <ul className="dropdown-menu dropdown-menu-end" aria-labelledby="userDropdown">
                <li className="px-3 py-2">
                  <div className="fw-semibold text-body">{config.name || config.code || "Người dùng"}</div>
                </li>
                <li><hr className="dropdown-divider my-1" /></li>
                <li><Link className="dropdown-item d-none" to="/contacts">Đối tác</Link></li>
                {canViewSettings && (
                  <li>
                    <Link className="dropdown-item" to="/setting-progress">
                      <FiSettings size={18} className="me-2" /> Cài đặt
                    </Link>
                  </li>
                )}
                {canViewSystemSettings && (
                  <li>
                    <Link className="dropdown-item" to="/cai-dat-he-thong">
                      <FiSliders size={18} className="me-2" /> Cài đặt hệ thống
                    </Link>
                  </li>
                )}
                <li>
                  <Link className="dropdown-item" to="/ho-so-cua-toi">
                    <FiUser size={18} className="me-2" /> Hồ sơ của tôi
                  </Link>
                </li>
                <li>
                  <a className="dropdown-item" onClick={handleLogout}>
                    <FiLogOut size={18} className="me-2" /> Đăng xuất
                  </a>
                </li>
              </ul>
            </li>
          </ul>
        </div>
      </div>
    </nav>
  );
};

export default BackendHeader;
